import * as React from 'react'
import { ReactElement, useEffect, useState } from 'react'
import BaseCombobox, { SubComboboxProps } from '../../../components/Combobox/BaseCombobox'
import { formatFolderName } from '../../../helpers/documentHelper'
import { fetchFolderDriveItemTree, FolderDriveItem } from '../../../components/Document/DocumentFlatTree'
import useAxios from '../../../hooks/useAxios'

export enum DocumentSelectionType {
    // noinspection JSUnusedGlobalSymbols
    FolderOnly = 'FOLDER_ONLY',
    DocumentOnly = 'DOCUMENT_ONLY',
    Both = 'BOTH',
}

export type DocumentComboboxProps = SubComboboxProps<FolderDriveItem> & {
    selectionType?: DocumentSelectionType
    documentRootId?: string
    onLazyLoaded?: (items: FolderDriveItem[]) => void
}

export const getEntityIds = (children: FolderDriveItem[]): string[] => {
    return children.flatMap(child => [child.id, ...getEntityIds(child.children || [])])
}

export default function DocumentCombobox(props: DocumentComboboxProps): ReactElement {
    const axios = useAxios()

    const [lazyEntities, setLazyEntities] = useState<FolderDriveItem[]>()
    useEffect(() => {
        if (!props.entities) {
            fetchFolderDriveItemTree(axios, props.documentRootId, props.selectionType === DocumentSelectionType.FolderOnly).then(response => {
                setLazyEntities(response)
                props.onLazyLoaded?.(response)
            })
        } else {
            setLazyEntities(props.entities)
        }
    }, [props.entities, props.documentRootId])

    const rootEntities = React.useMemo(() => {
        const childIds = getEntityIds(lazyEntities ?? [])
        return lazyEntities?.filter(entity => !childIds.includes(entity.parent?.id)) ?? []
    }, [lazyEntities])

    return (
        <BaseCombobox<FolderDriveItem>
            filter={entity => {
                switch (props.selectionType ?? DocumentSelectionType.Both) {
                    case DocumentSelectionType.FolderOnly:
                        return entity.itemType === 'branch'
                    case DocumentSelectionType.DocumentOnly:
                        return entity.itemType === 'leaf'
                }
                return true
            }}
            isGroup={entity => entity.itemType === 'branch'}
            isSelectableGroup={entity =>
                entity.itemType === 'branch' && (props.selectionType ?? DocumentSelectionType.Both) !== DocumentSelectionType.DocumentOnly
            }
            getGroupLabel={entity => (entity.itemType === 'branch' ? formatFolderName(entity.name) : entity.name)}
            getGroupValue={entity => entity.id}
            getGroupEntities={entity => entity.children}
            getEntityLabel={entity => (entity.itemType === 'branch' ? formatFolderName(entity.name) : entity.name)}
            getEntityValue={entity => entity.id}
            placeholderKey={`base.documentcombobox.${props.selectionType.toLowerCase()}.placeholder`}
            {...props}
            entities={rootEntities}
        />
    )
}
